import {
  isDragAndDropPin,
  isOpenQuestionPin,
  isQuizPin,
  isSortingQuestionPin,
  openQuestionAllCorrectAnswers,
  TeacherPin,
} from '@lessonup/pins-shared';
import {
  AnyView,
  areAllAnswerByCategoryAnswersRevealed,
  areAllWordCloudAnswersRevealed,
  isAnswerByCategoryView,
  isOpenQuestionView,
  isWordCloudView,
  PlayerBarBottomSection,
  PlayerStore,
  PlayerStoreDispatcher,
  useCurrentPin,
  useCurrentPinId,
  useCurrentPinState,
  usePlayerDispatcher,
  usePlayerStore,
} from '@lessonup/players-modern';
import {
  Button,
  IconUndo,
  styled,
  Switch,
  IconClipboard,
  IconVisibilityOff,
  IconVisibilityOn,
  IconStop,
} from '@lessonup/ui-components';
import { TFunction } from 'i18next';
import React from 'react';
import {
  AnswerAreaComponentHandlers,
  useMaximiseAnswerAreaComponent,
} from '../../../../RealtimePlayer/hooks/useMaximiseAnswerAreaComponent';
import { usePlayerTranslation } from '../../../../hooks/usePlayerTranslation';
import { WaitingForStudents } from '../WaitingForStudents/WaitingForStudents';

interface PlayerBarBottomInteractiveStepControlsProps {
  totalStudents?: number;
  currentView: AnyView | null;
  previousStep?: (e: React.MouseEvent) => void;
  nextStep?: (e: React.MouseEvent) => Promise<void>;
}

export const PlayerBarBarBottomInteractiveStepControls: React.FC<PlayerBarBottomInteractiveStepControlsProps> = ({
  currentView,
  previousStep,
  nextStep,
}) => {
  const { t } = usePlayerTranslation();
  const dispatcher = usePlayerDispatcher();
  const currentPinId = useCurrentPinId();
  const currentPin = useCurrentPin();
  const answerAreaComponentHandlers = useMaximiseAnswerAreaComponent();

  if (!currentView || !currentPinId || !currentPin) return null;

  if (currentView.phase === 'input') {
    return (
      <PlayerBarBottomSection>
        <WaitingForStudents />
        <Button onClick={nextStep} iconStart={<IconStop />} aria-label={t('labels.endResponses')}>
          {t('labels.endResponses')}
        </Button>
        <RevealAllAnswersButton currentView={currentView} currentPin={currentPin} dispatcher={dispatcher} t={t} />
        <MaximiseAnswerAreaSwitch
          currentView={currentView}
          answerAreaComponentHandlers={answerAreaComponentHandlers}
          t={t}
        />
      </PlayerBarBottomSection>
    );
  }

  if (currentView.phase === 'discuss' || (!hasDiscussionStep(currentPin) && currentView.phase === 'results')) {
    return (
      <PlayerBarBottomSection>
        <Button
          buttonType="neutral"
          buttonShape="rect"
          onClick={previousStep}
          iconStart={<IconUndo />}
          aria-label={t('labels.allowAnswers')}
        >
          {t('labels.allowAnswers')}
        </Button>
        {currentView.phase === 'discuss' && (
          <Button onClick={nextStep} iconStart={<IconClipboard />} aria-label={t('labels.viewResults')}>
            {t('labels.viewResults')}
          </Button>
        )}
        <RevealAllAnswersButton currentView={currentView} currentPin={currentPin} dispatcher={dispatcher} t={t} />
        <MaximiseAnswerAreaSwitch
          currentView={currentView}
          answerAreaComponentHandlers={answerAreaComponentHandlers}
          t={t}
        />
      </PlayerBarBottomSection>
    );
  }

  if (currentView.phase === 'results') {
    return (
      <PlayerBarBottomSection>
        <Button
          buttonType="neutral"
          buttonShape="rect"
          onClick={previousStep}
          iconStart={<IconUndo />}
          aria-label={t('labels.hideResults')}
        >
          {t('labels.hideResults')}
        </Button>
        <RevealAllAnswersButton currentView={currentView} currentPin={currentPin} dispatcher={dispatcher} t={t} />
        <MaximiseAnswerAreaSwitch
          currentView={currentView}
          answerAreaComponentHandlers={answerAreaComponentHandlers}
          t={t}
        />
      </PlayerBarBottomSection>
    );
  }
};

function areAllAnswersRevealed(view: AnyView, store: PlayerStore, currentPin: TeacherPin): boolean {
  if (isWordCloudView(view)) {
    return areAllWordCloudAnswersRevealed(store);
  }
  if (isOpenQuestionView(view)) {
    const allStudentAnswersRevealed = view.revealedAnswers.length === view.answerOrder.length;
    const hasCorrectAnswers = hasDiscussionStep(currentPin);
    return hasCorrectAnswers
      ? allStudentAnswersRevealed && view.isCorrectAnswersCardRevealed
      : allStudentAnswersRevealed;
  }
  if (isAnswerByCategoryView(view)) {
    return areAllAnswerByCategoryAnswersRevealed(store);
  }
  return false;
}

function hasNoAnswers(
  currentView: AnyView,
  pinState: ReturnType<typeof useCurrentPinState>,
  currentPin: TeacherPin
): boolean {
  if (isWordCloudView(currentView)) {
    return !pinState?.wordCloud?.answers?.length;
  }
  if (isOpenQuestionView(currentView)) {
    const hasNoStudentAnswers = currentView.answerOrder.length === 0;
    return hasNoStudentAnswers && !hasDiscussionStep(currentPin);
  }
  if (isAnswerByCategoryView(currentView)) {
    return !pinState?.answerByCategory?.answers?.length;
  }
  return true;
}

function hasDiscussionStep(currentPin: TeacherPin): boolean {
  if (isQuizPin(currentPin) || isSortingQuestionPin(currentPin)) {
    return true;
  }
  if (isOpenQuestionPin(currentPin)) {
    return openQuestionAllCorrectAnswers(currentPin.teacherSettings).length > 0;
  }
  if (isDragAndDropPin(currentPin)) {
    const correctAnswers = currentPin.teacherSettings.correctAnswers;
    return Object.keys(correctAnswers).length > 0;
  }
  return false;
}

const RevealAllAnswersButton: React.FC<{
  currentView: AnyView;
  currentPin: TeacherPin;
  dispatcher: PlayerStoreDispatcher;
  t: TFunction;
}> = ({ currentView, currentPin, dispatcher, t }) => {
  const playerStoreInterface = usePlayerStore();
  const currentPinState = useCurrentPinState();

  if (hasNoAnswers(currentView, currentPinState, currentPin)) {
    return null;
  }

  const allAnswersRevealed = areAllAnswersRevealed(currentView, playerStoreInterface, currentPin);
  const icon = allAnswersRevealed ? <IconVisibilityOff /> : <IconVisibilityOn />;

  return (
    <Button
      showStroke
      iconStart={icon}
      onClick={(e) => {
        e.stopPropagation();
        dispatcher({
          type: 'pinTypeAction',
          data: {
            type: allAnswersRevealed ? 'hideAllAnswers' : 'revealAllAnswers',
          },
        });
      }}
      buttonType="neutral"
    >
      {t(allAnswersRevealed ? 'labels.hideAllAnswers' : 'labels.revealAllAnswers')}
    </Button>
  );
};

const MaximiseAnswerAreaSwitch: React.FC<{
  currentView: AnyView;
  answerAreaComponentHandlers: AnswerAreaComponentHandlers;
  t: TFunction;
}> = ({ currentView, answerAreaComponentHandlers, t }) => {
  if (!isOpenQuestionView(currentView)) {
    return null;
  }

  return (
    <StyledSwitch
      id="maximiseAnswerArea"
      onClick={(e) => {
        if (e.currentTarget.checked) {
          answerAreaComponentHandlers.maximise();
        } else {
          answerAreaComponentHandlers.minimise();
        }
      }}
      label={t('labels.maximiseAnswerArea')}
    />
  );
};

const StyledSwitch = styled(Switch)`
  display: flex;
`;
